import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { CategoryRead, CategoryWrite, InputMaxLength } from "types"
import Callout from "components/callout/callout"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import useStorages from "hooks/useStorages"
import useCategories from "hooks/useCategories"
import useStorageLimits from "hooks/useStorageLimits"
import CategorySelector from "components/inputs/categorySelector"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import Button from "components/inputs/button"
import useLayout from "hooks/useLayout"
import { logger } from "utils/secureLogger"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import { ReactComponent as UpgradeIcon } from "assets/icons/upgrade.svg"

interface CreateCategoryFormProps {
    storageId: string
    defaultParentCategory?: string
    onSuccess?: () => void
}

const CreateCategoryForm: React.FC<CreateCategoryFormProps> = ({ storageId, defaultParentCategory, onSuccess }) => {

    const { t } = useTranslation()
    const state = useLocation().state as { categoryId: string }

    // React hooks.
    const navigation = useNavigate()

    // Component state.
    const [categoryId, setCategoryId] = useState<number>(1)
    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [storageRequirements, setStorageRequirements] = useState(``)
    const [notes, setNotes] = useState(``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Layout hooks.
    const { addToast } = useLayout()

    const {
        lastAddedCategoryId
    } = useStorages({ storageId })

    useEffect(() => {
        setCategoryId(lastAddedCategoryId ? lastAddedCategoryId + 1 : 1)
    }, [lastAddedCategoryId])

    const {
        categories,
        allCategories,
        createCategoryAsync,
        isCreating
    } = useCategories({ storageId })
    
    const { storage } = useStorages({ storageId })
    
    // Get category count for account limits check (per storage)
    const currentCategoryCount = allCategories?.length || 0
    const storagePlan = storage?.settings?.storagePlan
    const { canAddCategory, limits } = useStorageLimits({ 
        storagePlan,
        currentCategoryCount 
    })
    
    const [parentCategory, setParentCategory] = useState<string>(
        defaultParentCategory ?? (state?.categoryId ? state.categoryId : `_`)
    )

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Create category form submitted', undefined, 'CreateCategory')
        setErrors([])
        event.preventDefault()
        if (storageId === undefined) return
        
        // Check category limit before submitting
        if (!canAddCategory) {
            setErrors([t('errorCategoryLimitReached')])
            addToast({ text: t('errorCategoryLimitReached'), colorScheme: 'red' })
            return
        }
        
        try {
            const data: CategoryWrite = {
                ids: {
                    storageId: storageId,
                },
                _version: 1,
                categoryId,
                name: name,
                description: description,
                storageRequirements: storageRequirements,
                notes: notes,
                relations: {
                    parentCategory: parentCategory === `_` ? undefined : parentCategory,
                },
                isDeleted: false,
            }
            logger.debug('Category data prepared', data, 'CreateCategory')
            const result = await createCategoryAsync(data) as CategoryRead

            // Get the category ID from the result
            const createdCategoryId = result.categoryId
            
            logger.info('Category created successfully', { createdCategory: result }, 'CreateCategory')
            addToast({ text: t('toastCategoryCreated'), colorScheme: 'green' })
            if (onSuccess) {
                onSuccess()
            } else {
                navigation(`/storage/categories/${createdCategoryId}`)
            }
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastCategoryError'), colorScheme: 'red' })
            logger.error('Failed to create category', error)
        }
    }

    // Handle id change.
    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1
        setCategoryId(numericValue)
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    // Handle storage requirements change.
    const handleStorageRequirementsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStorageRequirements(e.target.value)
    }

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Return the JSX.
    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isCreating}
        >
            {!canAddCategory && (
                <Section>
                    <Callout type="warning">
                        {t('errorCategoryLimitReached', { limit: limits.categories })}
                        <Button wideOnMobile to={`/storage/edit`} design="primary"><UpgradeIcon /><span>{t('buttonUpgradeStoragePlan')}</span></Button>
                    </Callout>
                </Section>
            )}
            <Section>
                <TextInput label={t('id')} required={true} value={categoryId.toString()} onChange={handleIdChange} maxLength={InputMaxLength.short} />
                <TextInput label={t('labelCategoryName')} required={true} value={name} onChange={handleNameChange} maxLength={InputMaxLength.short} />
                <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={InputMaxLength.long} />
                <TextInput label={t('storageRequirements')} value={storageRequirements} onChange={handleStorageRequirementsChange} maxLength={InputMaxLength.long} />
                <TextAreaInput label={t('notes')} value={notes} onChange={handleNotesChange} maxLength={InputMaxLength.extraLong} />
                <div className={`input-holder-wide`}>
                    <label>{t('labelParentCategory')}</label>
                    <CategorySelector
                        categories={categories}
                        allCategories={allCategories}
                        selectedCategory={parentCategory}
                        setSelectedCategory={setParentCategory}
                        allowCreate={false}
                    />
                </div>
            </Section>
            <Section>
                <div className={`input-holder`}>
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!canAddCategory}><AddIcon /><span>{t('buttonCreateCategory')}</span></Button>
                    )}
                </div>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default CreateCategoryForm



